import { useCameraDrivers } from '../../hooks/useCameraDrivers';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { authApi, companyApi, userApi, cameraApi } from '../../services/api';
import { useAppStore } from '../../store/appStore';
import Avatar from '../common/Avatar';
import { CHAT_OPACITY_KEY, CHAT_OPACITY_EVENT } from '../camera/ChatPanel';

// IANA timezones (native). Used for operator/home timezone selectors.
const TZ_OPTIONS = (typeof Intl.supportedValuesOf === 'function')
  ? Intl.supportedValuesOf('timeZone')
  : ['America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York', 'UTC'];
const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Downscale a chosen image to a small square-ish JPEG data URL before upload,
// keeping avatars uniform and the payload tiny.
function resizeImage(file, max = 256) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = e => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const ROLE_LABELS = {
  admin: 'Admin',
  customer_admin: 'Customer Admin',
  customer_operator: 'Operator',
  volunteer_operator: 'Volunteer Operator',
  operator: 'Operator',
  viewer: 'Viewer',
};

const ROLE_COLORS = {
  admin: 'var(--accent-orange)',
  customer_admin: 'var(--accent-cyan)',
  customer_operator: 'var(--text-muted)',
  volunteer_operator: 'var(--accent-green)',
  operator: 'var(--text-muted)',
  viewer: 'var(--text-muted)',
};

function RoleBadge({ role }) {
  return (
    <span style={{
      fontFamily: 'var(--font-mono)', fontSize: '9px', fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.08em',
      padding: '2px 6px', borderRadius: '3px',
      color: ROLE_COLORS[role] || 'var(--text-muted)',
      border: `1px solid ${ROLE_COLORS[role] || 'var(--text-muted)'}`,
      opacity: 0.9,
    }}>
      {ROLE_LABELS[role] || role}
    </span>
  );
}

// ---- Company Modal ----
function CompanyModal({ company, onClose, onSaved }) {
  const [name, setName] = useState(company?.name || '');
  const [address, setAddress] = useState(company?.address || '');
  const [phone, setPhone] = useState(company?.phone || '');
  const [loading, setLoading] = useState(false);
  const { addToast } = useAppStore();

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    const payload = { name: name.trim(), address: address.trim(), phone: phone.trim() };
    try {
      if (company) {
        const res = await companyApi.update(company.id, payload);
        onSaved(res.data, 'edit');
        addToast({ type: 'success', message: `Company "${name.trim()}" updated` });
      } else {
        const res = await companyApi.create(payload);
        onSaved(res.data, 'add');
        addToast({ type: 'success', message: `Company "${name.trim()}" created` });
      }
      onClose();
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to save company' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: '400px' }}>
        <div className="modal-header">
          <h2>{company ? 'Edit Company' : 'Add Company'}</h2>
          <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div>
              <label className="label">Company Name *</label>
              <input className="input-field" autoFocus value={name} onChange={e => setName(e.target.value)} required placeholder="Acme Corp" />
            </div>
            <div style={{ marginTop: '12px' }}>
              <label className="label">Address <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
              <input className="input-field" value={address} onChange={e => setAddress(e.target.value)} placeholder="123 Main St, City, State" />
            </div>
            <div style={{ marginTop: '12px' }}>
              <label className="label">Phone <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
              <input className="input-field" value={phone} onChange={e => setPhone(e.target.value)} placeholder="(555) 123-4567" />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !name.trim()}>
              {loading ? 'Saving...' : company ? 'Save Changes' : 'Create Company'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---- User Modal ----
function UserModal({ editUser, companies, cameras, onClose, onSaved }) {
  const drivers = useCameraDrivers();
  const { addToast } = useAppStore();
  const [form, setForm] = useState({
    username: editUser?.username || '',
    password: '',
    firstName: editUser?.firstName || '',
    lastName: editUser?.lastName || '',
    email: editUser?.email || '',
    phone: editUser?.phone || '',
    role: editUser?.role || 'customer_operator',
    companyId: editUser?.companyId || '',
    allowedCameras: editUser?.allowedCameras || [],
    canReboot: editUser?.canReboot || false,
    timezone: editUser?.timezone || '',
  });
  const [loading, setLoading] = useState(false);
  const [avatar, setAvatar] = useState(editUser?.avatar || null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const avatarInputRef = useRef(null);

  async function handleAvatarFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editUser) return;
    if (!file.type.startsWith('image/')) { addToast({ type: 'error', message: 'Please choose an image file' }); return; }
    setAvatarLoading(true);
    try {
      const dataUrl = await resizeImage(file);
      const res = await userApi.uploadAvatar(editUser.id, dataUrl);
      setAvatar(res.data.avatar);
      onSaved({ ...editUser, avatar: res.data.avatar }, 'edit');
      addToast({ type: 'success', message: `Avatar updated for ${editUser.username}` });
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to upload avatar' });
    } finally {
      setAvatarLoading(false);
    }
  }

  async function handleRemoveAvatar() {
    if (!editUser) return;
    setAvatarLoading(true);
    try {
      await userApi.removeAvatar(editUser.id);
      setAvatar(null);
      onSaved({ ...editUser, avatar: null }, 'edit');
      addToast({ type: 'success', message: `Avatar removed for ${editUser.username}` });
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to remove avatar' });
    } finally {
      setAvatarLoading(false);
    }
  }

  const update = (field, val) => setForm(f => ({ ...f, [field]: val }));
  const toggleCamera = (id) => setForm(f => ({
    ...f,
    allowedCameras: f.allowedCameras.includes(id)
      ? f.allowedCameras.filter(c => c !== id)
      : [...f.allowedCameras, id],
  }));

  const isVolunteer = form.role === 'volunteer_operator';

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        companyId: (form.role === 'admin' || isVolunteer) ? null : (form.companyId || null),
        allowedCameras: isVolunteer ? form.allowedCameras : [],
        password: form.password || undefined,
      };
      if (editUser) {
        const res = await userApi.update(editUser.id, payload);
        onSaved(res.data, 'edit');
        addToast({ type: 'success', message: `User "${form.username}" updated` });
      } else {
        if (!form.password) { addToast({ type: 'error', message: 'Password required for new users' }); setLoading(false); return; }
        const res = await userApi.create(payload);
        onSaved(res.data, 'add');
        addToast({ type: 'success', message: `User "${form.username}" created` });
      }
      onClose();
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to save user' });
    } finally {
      setLoading(false);
    }
  }

  const showCompany = form.role !== 'admin' && !isVolunteer;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h2>{editUser ? 'Edit User' : 'Add User'}</h2>
          <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {editUser ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <Avatar src={avatar} name={form.username} userId={editUser.id} size={56} />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input ref={avatarInputRef} type="file" accept="image/*" onChange={handleAvatarFile} style={{ display: 'none' }} />
                  <button type="button" className="btn btn-ghost btn-sm" disabled={avatarLoading} onClick={() => avatarInputRef.current?.click()}>
                    {avatarLoading ? 'Uploading...' : (avatar ? 'Change Photo' : 'Upload Photo')}
                  </button>
                  {avatar && (
                    <button type="button" className="btn btn-ghost btn-sm" disabled={avatarLoading} onClick={handleRemoveAvatar} style={{ color: 'var(--accent-red)' }}>
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ) : null}
            <div className="grid-2">
              <div>
                <label className="label">First Name</label>
                <input className="input-field" value={form.firstName} onChange={e => update('firstName', e.target.value)} autoFocus />
              </div>
              <div>
                <label className="label">Last Name</label>
                <input className="input-field" value={form.lastName} onChange={e => update('lastName', e.target.value)} />
              </div>
            </div>
            <div className="grid-2">
              <div>
                <label className="label">Username *</label>
                <input className="input-field" value={form.username} onChange={e => update('username', e.target.value)} required />
              </div>
              <div>
                <label className="label">Password {editUser && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(blank = keep)</span>}</label>
                <input className="input-field" type="password" value={form.password} onChange={e => update('password', e.target.value)} minLength={editUser ? 0 : 6} placeholder={editUser ? 'Leave blank to keep' : ''} />
              </div>
            </div>
            <div className="grid-2">
              <div>
                <label className="label">Email</label>
                <input className="input-field" type="email" value={form.email} onChange={e => update('email', e.target.value)} />
              </div>
              <div>
                <label className="label">Phone</label>
                <input className="input-field" value={form.phone} onChange={e => update('phone', e.target.value)} />
              </div>
            </div>
            <div className="grid-2">
              <div>
                <label className="label">Role</label>
                <select className="input-field select-field" value={form.role} onChange={e => update('role', e.target.value)}>
                  <option value="admin">Admin</option>
                  <option value="customer_admin">Customer Admin</option>
                  <option value="customer_operator">Customer Operator</option>
                  <option value="volunteer_operator">Volunteer Operator</option>
                </select>
              </div>
              {showCompany && (
                <div>
                  <label className="label">Company</label>
                  <select className="input-field select-field" value={form.companyId} onChange={e => update('companyId', e.target.value)}>
                    <option value="">No Company</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
            </div>

            <div>
              <label className="label">Timezone <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(operator's home timezone, used by the schedule)</span></label>
              <select className="input-field select-field" value={form.timezone} onChange={e => update('timezone', e.target.value)}>
                <option value="">Not set</option>
                {TZ_OPTIONS.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>

            {/* Volunteer Operator: per-user camera allow-list (no company) */}
            {isVolunteer && (
              <div>
                <label className="label">
                  Allowed Cameras <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({form.allowedCameras.length} selected)</span>
                </label>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-muted)', marginBottom: '6px' }}>
                  This volunteer can access and control only the cameras checked below.
                </div>
                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border-dim)', borderRadius: 'var(--radius-md)', padding: '6px' }}>
                  {cameras.length === 0 ? (
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-muted)', padding: '8px', textAlign: 'center' }}>No cameras available</div>
                  ) : cameras.map(cam => (
                    <label key={cam.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 6px', cursor: 'pointer', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-display)', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      <input type="checkbox" checked={form.allowedCameras.includes(cam.id)} onChange={() => toggleCamera(cam.id)} style={{ width: '14px', height: '14px', cursor: 'pointer', flexShrink: 0 }} />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cam.name}</span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)' }}>{drivers?.[cam.manufacturer]?.displayName ?? drivers?.axis?.displayName ?? ''}</span>
                    </label>
                  ))}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', cursor: 'pointer', fontFamily: 'var(--font-display)', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  <input type="checkbox" checked={form.canReboot} onChange={e => update('canReboot', e.target.checked)} style={{ width: '14px', height: '14px', cursor: 'pointer', flexShrink: 0 }} />
                  Allow this volunteer to reboot their cameras
                </label>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : editUser ? 'Save Changes' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---- Main Component ----
export default function SettingsPanel({ section = 'account' }) {
  const { user, logout, addToast, setUser } = useAppStore();
  const [avatarLoading, setAvatarLoading] = useState(false);
  const avatarInputRef = useRef(null);

  // Self-serve profile (Account)
  const [profile, setProfile] = useState({
    firstName: user?.firstName || '', lastName: user?.lastName || '',
    email: user?.email || '', phone: user?.phone || '',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  // Re-seed the form when the logged-in user resolves/changes (AppShell can render
  // before /me populates `user`, which would otherwise leave the form blank).
  useEffect(() => {
    if (user) setProfile({ firstName: user.firstName || '', lastName: user.lastName || '', email: user.email || '', phone: user.phone || '' });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  async function handleSaveProfile(e) {
    e.preventDefault();
    setProfileSaving(true);
    try {
      const res = await authApi.updateProfile(profile);
      setUser({ ...user, ...res.data });
      addToast({ type: 'success', message: 'Profile updated' });
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to update profile' });
    } finally {
      setProfileSaving(false);
    }
  }

  async function handleAvatarFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';  // allow re-selecting the same file later
    if (!file) return;
    if (!file.type.startsWith('image/')) { addToast({ type: 'error', message: 'Please choose an image file' }); return; }
    setAvatarLoading(true);
    try {
      const dataUrl = await resizeImage(file);
      const res = await authApi.uploadAvatar(dataUrl);
      setUser({ ...user, avatar: res.data.avatar });
      addToast({ type: 'success', message: 'Avatar updated' });
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to upload avatar' });
    } finally {
      setAvatarLoading(false);
    }
  }

  async function handleRemoveAvatar() {
    setAvatarLoading(true);
    try {
      await authApi.removeAvatar();
      setUser({ ...user, avatar: null });
      addToast({ type: 'success', message: 'Avatar removed' });
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to remove avatar' });
    } finally {
      setAvatarLoading(false);
    }
  }
  // Account tab
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  // Appearance: live-chat panel transparency (0–100%)
  const [chatOpacity, setChatOpacity] = useState(
    () => parseInt(localStorage.getItem(CHAT_OPACITY_KEY) ?? '62', 10)
  );
  function handleChatOpacity(v) {
    setChatOpacity(v);
    localStorage.setItem(CHAT_OPACITY_KEY, String(v));
    // Notify any open chat panel to update live.
    window.dispatchEvent(new CustomEvent(CHAT_OPACITY_EVENT, { detail: v }));
  }

  // Self-serve home timezone (used by the schedule to show your local time).
  async function handleMyTimezone(tz) {
    try {
      const res = await authApi.setTimezone(tz || null);
      setUser({ ...user, timezone: res.data.timezone || null });
      addToast({ type: 'success', message: tz ? `Your timezone: ${tz}` : 'Timezone cleared' });
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to update timezone' });
    }
  }

  // Users & Companies tab
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [companyModal, setCompanyModal] = useState(null); // null | { mode: 'add' } | { mode: 'edit', company }
  const [userModal, setUserModal] = useState(null);       // null | { mode: 'add' } | { mode: 'edit', user }

  const loadUsersAndCompanies = useCallback(async () => {
    setLoadingData(true);
    try {
      const [cRes, uRes, camRes] = await Promise.all([companyApi.list(), userApi.list(), cameraApi.list()]);
      setCompanies(cRes.data);
      setUsers(uRes.data);
      setCameras(camRes.data);
    } catch {
      addToast({ type: 'error', message: 'Failed to load users/companies' });
    } finally {
      setLoadingData(false);
    }
  }, [addToast]);

  useEffect(() => {
    if ((section === 'users' || section === 'companies') && user?.role === 'admin') {
      loadUsersAndCompanies();
    }
  }, [section, user?.role, loadUsersAndCompanies]);

  async function handleChangePassword(e) {
    e.preventDefault();
    if (newPw !== confirmPw) { addToast({ type: 'error', message: 'Passwords do not match' }); return; }
    if (newPw.length < 6) { addToast({ type: 'error', message: 'Password must be at least 6 characters' }); return; }
    setPwLoading(true);
    try {
      await authApi.changePassword(currentPw, newPw);
      addToast({ type: 'success', message: 'Password changed. Please log in again.' });
      setTimeout(logout, 1500);
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to change password' });
    } finally {
      setPwLoading(false);
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    }
  }

  async function handleDeleteCompany(co) {
    if (!window.confirm(`Delete company "${co.name}"? This will not delete cameras or users — they will just lose their company assignment.`)) return;
    try {
      await companyApi.delete(co.id);
      setCompanies(prev => prev.filter(c => c.id !== co.id));
      addToast({ type: 'success', message: `Company "${co.name}" deleted` });
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to delete company' });
    }
  }

  async function handleDeleteUser(u) {
    if (!window.confirm(`Delete user "${u.username}"?`)) return;
    try {
      await userApi.delete(u.id);
      setUsers(prev => prev.filter(x => x.id !== u.id));
      addToast({ type: 'success', message: `User "${u.username}" deleted` });
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to delete user' });
    }
  }

  function onCompanySaved(co, mode) {
    if (mode === 'edit') {
      setCompanies(prev => prev.map(c => c.id === co.id ? { ...c, ...co } : c));
    } else {
      setCompanies(prev => [...prev, co]);
    }
  }

  function onUserSaved(u, mode) {
    if (mode === 'edit') {
      setUsers(prev => prev.map(x => x.id === u.id ? u : x));
    } else {
      setUsers(prev => [...prev, u]);
    }
  }

  const companyName = (id) => companies.find(c => c.id === id)?.name || '—';

  const sectionCard = { background: 'var(--bg-surface)', border: '1px solid var(--border-dim)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' };
  const sectionHeader = { padding: '14px 20px', borderBottom: '1px solid var(--border-dim)', fontFamily: 'var(--font-display)', fontSize: '13px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-primary)' };
  const TITLES = { account: 'My Account', users: 'Users', companies: 'Companies', system: 'System' };

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--accent-cyan)', textTransform: 'uppercase', marginBottom: '20px' }}>
          {TITLES[section] || 'Settings'}
        </div>

        {/* Account */}
        {section === 'account' && (
          <div style={{ maxWidth: '640px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={sectionCard}>
              <div style={sectionHeader}>Profile</div>
              <form onSubmit={handleSaveProfile} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div className="grid-2">
                  <div>
                    <label className="label">First Name</label>
                    <input className="input-field" value={profile.firstName} onChange={e => setProfile(p => ({ ...p, firstName: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label">Last Name</label>
                    <input className="input-field" value={profile.lastName} onChange={e => setProfile(p => ({ ...p, lastName: e.target.value }))} />
                  </div>
                </div>
                <div className="grid-2">
                  <div>
                    <label className="label">Email</label>
                    <input className="input-field" type="email" value={profile.email} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label">Phone</label>
                    <input className="input-field" value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={profileSaving}>
                    {profileSaving ? 'Saving...' : 'Save Profile'}
                  </button>
                </div>
              </form>
            </div>

            <div style={sectionCard}>
              <div style={sectionHeader}>Profile Photo</div>
              <div style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: '18px' }}>
                <Avatar src={user?.avatar} name={user?.username} userId={user?.id} size={72} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-muted)' }}>
                    Shown next to your name in the sidebar and when you control a camera.
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input ref={avatarInputRef} type="file" accept="image/*" onChange={handleAvatarFile} style={{ display: 'none' }} />
                    <button className="btn btn-primary btn-sm" disabled={avatarLoading} onClick={() => avatarInputRef.current?.click()}>
                      {avatarLoading ? 'Uploading...' : (user?.avatar ? 'Change Photo' : 'Upload Photo')}
                    </button>
                    {user?.avatar && (
                      <button className="btn btn-ghost btn-sm" disabled={avatarLoading} onClick={handleRemoveAvatar}>
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div style={sectionCard}>
              <div style={sectionHeader}>
                Change Password
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', fontWeight: 400, color: 'var(--text-muted)', marginTop: '2px' }}>
                  Logged in as: {user?.username} ({ROLE_LABELS[user?.role] || user?.role})
                </div>
              </div>
              <form onSubmit={handleChangePassword} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label className="label">Current Password</label>
                  <input className="input-field" type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} required />
                </div>
                <div className="grid-2">
                  <div>
                    <label className="label">New Password</label>
                    <input className="input-field" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} required minLength={6} />
                  </div>
                  <div>
                    <label className="label">Confirm New Password</label>
                    <input className="input-field" type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required />
                  </div>
                </div>
                <div>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={pwLoading}>
                    {pwLoading ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </form>
            </div>

            <div style={sectionCard}>
              <div style={sectionHeader}>Appearance</div>
              <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label className="label" style={{ margin: 0 }}>Live Chat Transparency</label>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--accent-orange)' }}>
                    {100 - chatOpacity}%
                  </span>
                </div>
                <input
                  type="range" min="0" max="100" value={100 - chatOpacity}
                  style={{ width: '100%', accentColor: 'var(--accent-orange)' }}
                  onChange={e => handleChatOpacity(100 - parseInt(e.target.value, 10))}
                />
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-muted)' }}>
                  How see-through the chat panel is over the camera video. Drag right for more transparency.
                </div>
              </div>
            </div>

            <div style={sectionCard}>
              <div style={sectionHeader}>Your Timezone</div>
              <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <select className="input-field select-field" value={user?.timezone || ''} onChange={e => handleMyTimezone(e.target.value)}>
                  <option value="">Not set (detected: {browserTz})</option>
                  {TZ_OPTIONS.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </select>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-muted)' }}>
                  Your home timezone. The schedule shows each camera's slots in the camera's local time, and your equivalent time using this.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users / Companies (admin) */}
        {(section === 'users' || section === 'companies') && user?.role === 'admin' && (
          <div>
            {loadingData ? (
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--text-muted)', padding: '20px' }}>Loading...</div>
            ) : (
              <div style={{ maxWidth: '720px' }}>

                {/* Companies */}
                {section === 'companies' && (
                <div style={sectionCard}>
                  <div style={{ ...sectionHeader, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Companies ({companies.length})</span>
                    <button className="btn btn-ghost btn-sm" onClick={() => setCompanyModal({ mode: 'add' })}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      Add
                    </button>
                  </div>
                  <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                    {companies.length === 0 ? (
                      <div style={{ padding: '20px', fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center' }}>
                        No companies yet
                      </div>
                    ) : companies.map(co => (
                      <div key={co.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderBottom: '1px solid var(--border-dim)' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: 'var(--font-display)', fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {co.name}
                          </div>
                          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>
                            {co.cameraCount ?? 0} camera{(co.cameraCount ?? 0) !== 1 ? 's' : ''} · {co.userCount ?? 0} user{(co.userCount ?? 0) !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ padding: '3px 7px', fontSize: '10px' }}
                          onClick={() => setCompanyModal({ mode: 'edit', company: co })}
                          title="Edit company"
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ padding: '3px 7px', fontSize: '10px', color: 'var(--accent-red)' }}
                          onClick={() => handleDeleteCompany(co)}
                          title="Delete company"
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                )}

                {/* Users */}
                {section === 'users' && (
                <div style={sectionCard}>
                  <div style={{ ...sectionHeader, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Users ({users.length})</span>
                    <button className="btn btn-ghost btn-sm" onClick={() => setUserModal({ mode: 'add' })}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      Add
                    </button>
                  </div>
                  <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                    {users.map(u => (
                      <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderBottom: '1px solid var(--border-dim)' }}>
                        <Avatar src={u.avatar} name={u.username} userId={u.id} size={28} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                            <span style={{ fontFamily: 'var(--font-display)', fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>
                              {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.username}
                            </span>
                            <RoleBadge role={u.role} />
                          </div>
                          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: 'var(--text-muted)' }}>
                            @{u.username} · {u.role === 'volunteer_operator'
                              ? `${(u.allowedCameras || []).length} camera${(u.allowedCameras || []).length === 1 ? '' : 's'}`
                              : (u.companyId ? companyName(u.companyId) : 'No company')}
                            {u.email ? ` · ${u.email}` : ''}
                          </div>
                        </div>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ padding: '3px 7px', fontSize: '10px' }}
                          onClick={() => setUserModal({ mode: 'edit', user: u })}
                          title="Edit user"
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        {u.id !== user.id && (
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ padding: '3px 7px', fontSize: '10px', color: 'var(--accent-red)' }}
                            onClick={() => handleDeleteUser(u)}
                            title="Delete user"
                          >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                )}

              </div>
            )}
          </div>
        )}

        {/* System */}
        {section === 'system' && (
          <div style={{ maxWidth: '640px' }}>
            <div style={{ ...sectionCard, padding: '20px' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '14px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-primary)', marginBottom: '14px' }}>
                System Information
              </div>
              {[
                ['Version', '1.0.0'],
                ['Protocol', 'VAPIX v3 / RTSP'],
                ['Stream Method', 'FFmpeg → WebSocket/MPEG-TS'],
                ['Storage', 'JSON / Local File System'],
                ['Status Poll Interval', '30 seconds'],
              ].map(([label, value]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-dim)' }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>{label}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--text-secondary)' }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {companyModal && (
        <CompanyModal
          company={companyModal.mode === 'edit' ? companyModal.company : null}
          onClose={() => setCompanyModal(null)}
          onSaved={onCompanySaved}
        />
      )}
      {userModal && (
        <UserModal
          editUser={userModal.mode === 'edit' ? userModal.user : null}
          companies={companies}
          cameras={cameras}
          onClose={() => setUserModal(null)}
          onSaved={onUserSaved}
        />
      )}
    </div>
  );
}
